import { useCallback, useMemo, useState } from 'react';
import {
  IAlertThresholdCompareOp,
  ICreateScheduleMutation,
  ICreateScheduleMutationVariables,
  IScheduleConfigQuery,
  IScheduleConfigQueryVariables,
  IScheduleTypeLimit,
  IUpdateScheduleMutation,
  IUpdateScheduleMutationVariables,
  ScheduleType,
} from '../../utils/apollo/graphqlSchema';
import printGraphqlErrors from '../../utils/apollo/printGraphqlErrors';
import { useMutation, useQuery } from '@apollo/client';
import createScheduleMutation from '../../utils/apollo/scheduler/createScheduleMutation';
import { DictionaryTypeEnum } from '../../utils/interfaces';
import { updateDictionaryItemAction } from '../../redux/slices/dictionary';
import updateScheduleMutation from '../../utils/apollo/scheduler/updateScheduleMutation';
import { SCHEDULE_LIMITS_AND_NAMES } from '../../utils/apollo/scheduler/query';
import { useAppDispatch } from '../../redux/hooks';
import { useNavigate } from 'react-router-dom';
import { appRoutes } from '../../path';
import { assertNotNil } from '../../utils/types';
import toastMessage from '../../utils/toastMessage';
import { useAnalytics } from '../../utils/analytics/useAnalytics';
import { dashboardScheduleCreated } from '../../utils/analytics/events/dashboard/events';
import { reportScheduleCreated } from '../../utils/analytics/events/report/events';
import { UnreachableCaseError } from 'ts-essentials';

interface IProps {
  jobType: ScheduleType;
  currentScheduleName: string | null;
}

const scheduleTypeConfig: Record<
  ScheduleType,
  { url: string; dictionaryType: DictionaryTypeEnum }
> = {
  [ScheduleType.Analysis]: {
    url: appRoutes.schedules.report,
    dictionaryType: DictionaryTypeEnum.ScheduleAnalysis,
  },
  [ScheduleType.BrazeCohortImport]: {
    url: appRoutes.cohorts.view,
    dictionaryType: DictionaryTypeEnum.ScheduleCohort,
  },
  [ScheduleType.Dashboard]: {
    url: appRoutes.schedules.dashboard,
    dictionaryType: DictionaryTypeEnum.ScheduleDashboard,
  },
};

const useSchedulerApi = ({ jobType, currentScheduleName }: IProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { track } = useAnalytics();

  /* On create mutation callback */
  const onScheduleCreateCompleted = useCallback(
    ({ createSchedule }: ICreateScheduleMutation) => {
      const { schedule, errors: scheduleCreationErrors } = createSchedule;
      if (printGraphqlErrors(scheduleCreationErrors)) {
        return;
      } else {
        toastMessage({
          message: `${schedule?.name} has been created.`,
          type: 'info',
        });
      }
      const entity = schedule?.entity.item;
      if (entity) {
        if (entity.__typename === 'Dashboard') {
          track(dashboardScheduleCreated(entity));
        } else if (
          entity.__typename === 'Expression' ||
          entity.__typename === 'Funnel' ||
          entity.__typename === 'Path' ||
          entity.__typename === 'Retention' ||
          entity.__typename === 'Significance' ||
          entity.__typename === 'DataTable' ||
          entity.__typename === 'CohortSample' ||
          entity.__typename === 'Prediction'
        ) {
          track(reportScheduleCreated(entity));
        } else if (
          entity.__typename === 'ExpressionCohort' ||
          entity.__typename === 'FunnelCohort' ||
          entity.__typename === 'MeasureCohort' ||
          entity.__typename === 'PerformedCohort' ||
          entity.__typename === 'CompoundCohort' ||
          entity.__typename === 'RetentionCohort'
        ) {
          // no event for these (yet), this branch is only added for
          // exhaustive matching so when/if we add a new type, there will
          // compilation error
        } else {
          throw new UnreachableCaseError(entity);
        }
      }
      navigate(scheduleTypeConfig[jobType].url);
    },
    [jobType, navigate, track],
  );

  /* Create schedule mutation configuration */
  const [createSchedule, { loading: isCreateMutationLoading }] = useMutation<
    ICreateScheduleMutation,
    ICreateScheduleMutationVariables
  >(createScheduleMutation, {
    onCompleted: onScheduleCreateCompleted,
  });

  const onScheduleUpdateCompleted = useCallback(
    ({ updateSchedule }: IUpdateScheduleMutation) => {
      const { schedule, errors: scheduleUpdateErrors } = updateSchedule;
      if (printGraphqlErrors(scheduleUpdateErrors)) {
        return;
      }
      assertNotNil(schedule);

      toastMessage({
        message: `${schedule.name} has been updated.`,
        type: 'info',
      });

      setAllScheduleNames((prev) => {
        const filtered = prev.filter((v) => v !== currentScheduleName);
        return [...filtered, schedule.name];
      });

      dispatch(
        updateDictionaryItemAction({
          item: schedule,
          dictionaryType: scheduleTypeConfig[jobType].dictionaryType,
        }),
      );
    },
    [dispatch, jobType, currentScheduleName],
  );

  /* Update schedule mutation configuration */
  const [updateSchedule, { loading: isUpdateScheduleLoading }] = useMutation<
    IUpdateScheduleMutation,
    IUpdateScheduleMutationVariables
  >(updateScheduleMutation, {
    onCompleted: onScheduleUpdateCompleted,
  });

  const noLimit = useMemo((): IScheduleTypeLimit => {
    return {
      __typename: 'ScheduleTypeLimit',
      type: jobType,
      exceeded: false,
      maxCount: null,
    };
  }, [jobType]);

  /* flag indicating that limit of new schedules is exceeded */
  const [newScheduleLimit, setNewScheduleLimit] = useState(noLimit);
  const [allScheduleNames, setAllScheduleNames] = useState<string[]>([]);
  const [allThresholdCompareOps, setAllThresholdCompareOps] = useState<
    IAlertThresholdCompareOp[]
  >([]);

  const onScheduleConfigQueryCompleted = useCallback(
    ({ scheduleConfig }: IScheduleConfigQuery) => {
      const scheduleLimit = scheduleConfig?.limits.find(
        ({ type }) => type === jobType,
      );
      setNewScheduleLimit(scheduleLimit ?? noLimit);
      const allNames = scheduleConfig?.names ?? [];
      setAllScheduleNames(allNames);
      setAllThresholdCompareOps(scheduleConfig?.alertThresholdCompareOps ?? []);
    },
    [jobType, noLimit],
  );

  const { loading: isScheduleConfigLoading } = useQuery<
    IScheduleConfigQuery,
    IScheduleConfigQueryVariables
  >(SCHEDULE_LIMITS_AND_NAMES, {
    onCompleted: onScheduleConfigQueryCompleted,
  });

  return {
    isScheduleConfigLoading,
    allScheduleNames,
    allThresholdCompareOps,
    newScheduleLimit,
    createSchedule,
    isCreateMutationLoading,
    updateSchedule,
    isUpdateScheduleLoading,
  };
};

export default useSchedulerApi;
